import React, {Dispatch, SetStateAction, useMemo} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {
  Button,
  InputTextArea,
  InputTextFormik,
  SelectFormik,
} from '../../../../_gori/partials/widgets'
import {CLAIM_REASON_VALUES, ClaimsConfig, OPTION_ITEM_TYPE} from '../core/_const'
import {capitalize, map, sum} from 'lodash'
import {convertCurrency, formatBytes, KTSVG} from '../../../../_gori/helpers'
import clsx from 'clsx'
import {ViewUploadedOldFile} from '../components/ViewUploadedOldFile'

type ReviewClaimsModalProps = {
  formik: any
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  handleCreateClaim: () => void
  onCancelCallback: () => void
  trackingNumber: string
  optionClaimsReason: Array<{label: string; value: string}>
  mode?: 'create' | 'edit'
  isBaeGuardInsurance: boolean
  isLoadingForm?: boolean
}
export const ReviewClaimsModal = ({
  formik,
  show,
  setShow,
  handleCreateClaim,
  onCancelCallback,
  trackingNumber,
  optionClaimsReason,
  mode = 'create',
  isBaeGuardInsurance,
  isLoadingForm = false,
}: ReviewClaimsModalProps) => {
  const intl = useIntl()

  const handleCloseModal = () => {
    setShow(false)
  }

  const claimInvoices = useMemo(() => {
    if (mode === 'create') {
      return formik?.values?.claim_invoice || []
    }

    return [
      ...(formik?.values?.claim_init_invoice || []),
      ...(formik?.values?.claim_invoice_new || []),
    ]
  }, [
    formik?.values?.claim_invoice,
    formik?.values?.claim_init_invoice,
    formik?.values?.claim_invoice_new,
    mode,
  ])

  const photoItems = useMemo(() => {
    const result: Record<string, any> = {}

    Object.entries(formik?.values?.items).forEach(([key, item]: [string, any]) => {
      const photos =
        mode === 'edit'
          ? [...(item.photos_init || []), ...(item.photos_new || [])]
          : [...(item.photos || [])]

      result[key] = photos.map((item) => ({...item, url_show: item?.url_show ?? item.url}))
    })

    return result
  }, [formik?.values?.items, mode])

  const claimAmount = sum(
    map(formik.values.items, (item) => {
      const qty = Number(item.claim_item_qty)
      const price = Number(item.claim_item_price)
      return qty * price
    })
  )

  const disabledForm = true

  const claimReason = useMemo(() => {
    const values = formik.values.claim_reason

    const existed = optionClaimsReason.find((item) => item.value === values)

    return existed ? capitalize(existed.label) : intl.formatMessage({id: 'OTHER'})
  }, [formik.values.claim_reason, intl, optionClaimsReason])

  return (
    <div>
      <Modal
        id='gori_modal_review_claim'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-xl h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={onCancelCallback}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            <div className={'ms-2'}>
              <i
                className='bi bi-chevron-left text-primary fs-5 ms-5 cursor-pointer'
                onClick={handleCloseModal}
              ></i>
              <span className={'ms-3'}>{intl.formatMessage({id: 'CLAIM_FORM'})}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75 overflow-auto'>
          <div className={clsx({'cursor-not-allowed': disabledForm})}>
            <h1 className='mb-13 text-center'>{intl.formatMessage({id: 'PLEASE_REVIEW_CLAIM'})}</h1>
            <div className='border border-1 border-gray-300 rounded row overflow-hidden mb-5'>
              <div className='col-6 p-0'>
                <div className='p-4 fw-bold text-gray-700'>
                  {intl.formatMessage({id: 'TRACKING_NUMBER'})}
                </div>
                <div className='bg-gray-200 p-4'>{trackingNumber}</div>
              </div>
              <div className='col-2 p-0'>
                <div className='p-4 fw-bold text-gray-700'>
                  {intl.formatMessage({id: 'CLAIM_AMOUNT'})}
                </div>
                <div className='bg-gray-200 p-4'>{convertCurrency(claimAmount)}</div>
              </div>
              <div className='col-4 p-0'>
                <div className='p-4 fw-bold text-gray-700'>
                  {intl.formatMessage({id: 'CLAIM_REASON'})}
                </div>
                <div className='bg-gray-200 p-4'>{claimReason}</div>
              </div>
            </div>
            {isBaeGuardInsurance && (
              <div className='row mb-5'>
                <InputTextFormik
                  className='col-12'
                  labelClassName='col-form-label'
                  label={intl.formatMessage({id: 'CONSIGNEE_EMAIL'})}
                  placeholder={intl.formatMessage({id: 'CONSIGNEE_EMAIL'})}
                  formik={formik}
                  name={'consignee_email'}
                  checkFormik={false}
                  disabled={disabledForm}
                />
              </div>
            )}
            {formik.values.claim_reason === CLAIM_REASON_VALUES.DAMAGE && isBaeGuardInsurance && (
              <div className='row mb-5'>
                <InputTextArea
                  className='col-12'
                  inputClassName='h-100px'
                  labelClassName='col-form-label'
                  label={intl.formatMessage({id: 'DAMAGE_DESCRIPTION'})}
                  placeholder={intl.formatMessage({id: 'DAMAGE_DESCRIPTION_PLACEHOLDER'})}
                  formik={formik}
                  name={'damaged_description'}
                  checkFormik={false}
                  disabled={disabledForm}
                />
              </div>
            )}
            <h3 className='mt-15 mb-5'>{intl.formatMessage({id: 'ITEM_DETAILS'})}</h3>
            {Object.entries(formik.getFieldProps('items').value).map(([key, item], index) => {
              return (
                <div key={key} className='bg-light px-10 pt-5 pb-2 mb-4 rounded'>
                  <div className='d-flex align-items-center justify-content-between mb-5'>
                    <span className='fs-6 fw-bolder'>
                      {intl.formatMessage({id: 'ITEM'})} {index + 1}
                    </span>
                    <div className='d-flex align-items-center'>
                      <div
                        className='btn btn-sm btn-icon btn-white btn-active-light opacity-75 cursor-pointer rounded-circle'
                        onClick={() =>
                          formik.setFieldValue(
                            formik.getFieldProps(`items.${key}.show`).name,
                            !formik.getFieldProps(`items.${key}.show`).value
                          )
                        }
                      >
                        {formik.getFieldProps(`items.${key}.show`).value ? (
                          <KTSVG path='/media/gori/claims/down.svg' className='svg-icon-2' />
                        ) : (
                          <KTSVG path='/media/gori/claims/left.svg' className='svg-icon-2' />
                        )}
                      </div>
                    </div>
                  </div>

                  {formik.getFieldProps(`items.${key}.show`).value && (
                    <>
                      <div className='row mb-5'>
                        <div className='col-md-6'>
                          <div className='d-flex mb-5'>
                            <InputTextFormik
                              className='col-8'
                              labelClassName='col-4 col-form-label'
                              label={intl.formatMessage({id: 'ITEM_NAME'})}
                              formik={formik}
                              name={`items.${key}.claim_item_name`}
                              disabled={disabledForm}
                              checkFormik={false}
                            />
                          </div>
                          <div className='d-flex mb-5'>
                            <InputTextFormik
                              type={'number'}
                              min={0}
                              placeholder='$'
                              className='col-8'
                              labelClassName='col-4 col-form-label'
                              label={intl.formatMessage({id: 'UNIT_PRICE'})}
                              formik={formik}
                              name={`items.${key}.claim_item_price`}
                              isCurrency
                              disabled={disabledForm}
                              checkFormik={false}
                            />
                          </div>
                        </div>
                        <div className='col-md-6'>
                          <div className='d-flex mb-5'>
                            <SelectFormik
                              className='col-8'
                              labelClassName='col-4 col-form-label'
                              label={intl.formatMessage({id: 'ITEM_TYPE'})}
                              placeholder={intl.formatMessage(
                                {id: 'SELECT_A_INPUT'},
                                {input: intl.formatMessage({id: 'ITEM_TYPE'})}
                              )}
                              emptyDefault={false}
                              options={OPTION_ITEM_TYPE}
                              formik={formik}
                              name={`items.${key}.claim_item_type`}
                              hasUseIntl={true}
                              disabled={disabledForm}
                              checkFormik={false}
                            />
                          </div>
                          {formik.getFieldProps(`items.${key}.claim_item_type`).value ===
                            ClaimsConfig.ITEM_TYPE_OTHER && (
                            <div className='d-flex mb-5'>
                              <InputTextFormik
                                className='col-8'
                                labelClassName='col-4 col-form-label'
                                label={intl.formatMessage({id: 'SPECIFIC_ITEM_TYPE'})}
                                formik={formik}
                                name={`items.${key}.claim_item_specific_item_type`}
                                disabled={disabledForm}
                                checkFormik={false}
                              />
                            </div>
                          )}

                          <div className='d-flex'>
                            <InputTextFormik
                              type={'number'}
                              min={0}
                              isInteger
                              className='col-8'
                              labelClassName='col-4 col-form-label'
                              label={intl.formatMessage({id: 'QUANTITY'})}
                              formik={formik}
                              name={`items.${key}.claim_item_qty`}
                              disabled={disabledForm}
                              checkFormik={false}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <InputTextArea
                          className='col-12'
                          inputClassName='h-100px'
                          labelClassName='col-form-label'
                          label={intl.formatMessage({id: 'CLAIM_ITEM_DESCRIPTION'})}
                          formik={formik}
                          name={`items.${key}.claim_item_desc`}
                          disabled={disabledForm}
                          checkFormik={false}
                        />
                      </div>
                      <div className='col-form-label'>
                        {intl.formatMessage({
                          id:
                            formik.getFieldProps('claim_reason').value === 'damage'
                              ? 'PHOTO_OF_DAMAGED_ITEM'
                              : 'PHOTO_OF_ITEM',
                        })}
                      </div>
                      <div
                        className={clsx('d-inline-flex flex-column', {
                          'drop-zone mb-10': mode === 'create',
                        })}
                      >
                        {mode === 'edit' && (
                          <div className='d-flex gap-2 flex-wrap'>
                            <ViewUploadedOldFile
                              formik={formik}
                              nameField={`items.${key}.photos_init`}
                              allowDelete={false}
                            />
                            <ViewUploadedOldFile
                              formik={formik}
                              nameField={`items.${key}.photos_new`}
                              allowDelete={false}
                            />
                          </div>
                        )}
                        {mode === 'create' &&
                          photoItems?.[`${key}`]?.map((item: any) => {
                            return (
                              <div className='preview mb-2' key={`${key}_${item.id || item.key}`}>
                                <div className='preview__left'>
                                  <img src={item?.url_show} alt='img-file' />
                                  <span className='preview__left__name text-truncate'>
                                    {item.name}
                                  </span>
                                </div>
                                {mode === 'create' && <div>{formatBytes(item.size)}</div>}
                              </div>
                            )
                          })}
                      </div>
                    </>
                  )}
                </div>
              )
            })}
            <div className='mt-15 mb-5'>
              <h3>{intl.formatMessage({id: 'PURCHASE_INFORMATION'})}</h3>
            </div>
            <div className='mb-3'>
              <p>{intl.formatMessage({id: 'PROOF_OF_VALUE_INVOICE'})}</p>
            </div>
            <div
              className={clsx('mb-5 d-inline-flex flex-column', {
                'drop-zone': mode === 'create',
              })}
            >
              {mode === 'edit' && (
                <div className='d-flex gap-2 flex-wrap'>
                  <ViewUploadedOldFile
                    formik={formik}
                    nameField={'claim_init_invoice'}
                    allowDelete={false}
                  />
                  <ViewUploadedOldFile
                    formik={formik}
                    nameField={'claim_invoice_new'}
                    allowDelete={false}
                  />
                </div>
              )}
              {mode === 'create' &&
                claimInvoices?.map((item: any) => {
                  return (
                    <div className='preview mb-2' key={item.id || item.key}>
                      <div className='preview__left'>
                        <img src={item?.url} alt='img-file' />
                        <span className='preview__left__name text-truncate'>{item.name}</span>
                      </div>
                      <div>{formatBytes(item.size)}</div>
                    </div>
                  )
                })}
            </div>
            <h3 className='mt-5 mb-5'>
              {intl.formatMessage({id: 'REPLACEMENT_PACKAGE_INFORMATION'})}
            </h3>
            <div className='d-flex align-items-center mb-5'>
              <span className='fs-6 fw-semibold text-gray-800 d-block'>
                {intl.formatMessage({id: 'HAS_A_REPLACEMENT_PACKAGE_BEEN_SHIPPED'})}
              </span>
              <label className='form-check form-switch form-check-custom form-check-solid px-5'>
                <input
                  disabled={disabledForm}
                  className='form-check-input'
                  type='checkbox'
                  checked={formik.getFieldProps('claim_is_replace_package').value}
                  value={formik.getFieldProps('claim_is_replace_package').value}
                  onChange={formik.handleChange}
                  name='claim_is_replace_package'
                />
              </label>
            </div>
            {formik.getFieldProps('claim_is_replace_package').value ? (
              <div className='d-flex'>
                <InputTextFormik
                  className='col-9'
                  labelClassName='col-3 col-form-label'
                  label={intl.formatMessage({id: 'REPLACEMENT_TRACKING_NUMBER'})}
                  formik={formik}
                  name={'tracking_number_replace'}
                  disabled={disabledForm}
                  checkFormik={false}
                />
              </div>
            ) : (
              <div className='d-flex align-items-center mb-5'>
                <span className='fs-6 fw-semibold text-gray-800 d-block'>
                  {intl.formatMessage({id: 'DO_YOU_PLAN_ON_SHIPPING_A_RE_PLACEMENT_PACKAGE'})}
                </span>
                <label className='form-check form-switch form-check-custom form-check-solid px-5'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    checked={formik.getFieldProps('claim_is_plan_replace_package').value}
                    value={formik.getFieldProps('claim_is_plan_replace_package').value}
                    onChange={formik.handleChange}
                    name='claim_is_plan_replace_package'
                    disabled={disabledForm}
                  />
                </label>
              </div>
            )}
            <div className='mt-15 mb-5'>
              <h3>{intl.formatMessage({id: 'RECIPIENT_CONTACTS'})}</h3>
            </div>
            <div className='row mb-5'>
              <div className='col-md-6 mb-1'>
                <div className='d-flex'>
                  <InputTextFormik
                    className='col-8'
                    labelClassName='col-4 col-form-label'
                    label={intl.formatMessage({id: "RECIPIENT'S_PHONE"})}
                    formik={formik}
                    name='claim_recipient_phone'
                    disabled={disabledForm}
                    checkFormik={false}
                  />
                </div>
              </div>
              <div className='col-md-6'>
                <div className='d-flex'>
                  <InputTextFormik
                    className='col-8'
                    labelClassName='col-4 col-form-label'
                    label={intl.formatMessage({id: "RECIPIENT'S_EMAIL"})}
                    formik={formik}
                    name='claim_recipient_email'
                    disabled={disabledForm}
                    checkFormik={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-2'
              label={intl.formatMessage({id: 'CANCEL'})}
              loadingText={intl.formatMessage({id: 'CANCEL'})}
              event={() => {
                handleCloseModal()
                onCancelCallback()
              }}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({
                id: mode === 'create' ? 'REQUEST_CLAIM' : 'UPDATE_CLAIM',
              })}
              loadingText={intl.formatMessage({
                id: mode === 'create' ? 'REQUEST_CLAIM' : 'UPDATE_CLAIM',
              })}
              event={handleCreateClaim}
              loading={isLoadingForm}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
